import type { Engine } from "@scenix/components";

import geist400 from "@fontsource/geist/files/geist-latin-400-normal.woff?url";
import geist500 from "@fontsource/geist/files/geist-latin-500-normal.woff?url";
import geist700 from "@fontsource/geist/files/geist-latin-700-normal.woff?url";
import resvgWasm from "@resvg/resvg-wasm/index_bg.wasm?url";

/**
 * The engine that draws a component, in the browser (ADR 000069).
 *
 * The same engine the worker runs, given the same fonts and the same
 * WebAssembly, because that is the whole argument for choosing satori over a
 * headless browser: the preview is not *like* the render, it is the same code
 * producing the same PNG. `mise run check:components` holds the two to the
 * byte.
 *
 * Loaded on demand. Satori, resvg and harfbuzz are together larger than the
 * rest of the studio, and most projects have no component in them, so a person
 * looking at a chain of ordinary segments should pay nothing for this. The
 * dynamic import below is what keeps it out of the first chunk; the `?url`
 * imports above are only strings.
 */

const weights = [
  { url: geist400, weight: 400 as const },
  { url: geist500, weight: 500 as const },
  { url: geist700, weight: 700 as const },
];

let building: Promise<Engine> | undefined;

/**
 * The engine, built once per page.
 *
 * Memoised on the promise rather than the result, so two boxes asking at the
 * same moment share one download and one initialisation instead of racing to
 * do both twice. resvg's own setup is global to the page and throws if it runs
 * again, so this is load-bearing rather than a tidiness.
 */
export function componentEngine(): Promise<Engine> {
  building ??= (async () => {
    const [{ createEngine }, resvg, ...fonts] = await Promise.all([
      import("@scenix/components"),
      fetch(resvgWasm).then((r) => r.arrayBuffer()),
      ...weights.map(({ url }) => fetch(url).then((r) => r.arrayBuffer())),
    ]);

    return createEngine({
      resvgWasm: resvg,
      // Zipped with the weights they were fetched from, in the order they were
      // asked for, rather than indexed back out of the array.
      fonts: weights.map(({ weight }, i) => ({
        name: "Geist",
        data: fonts[i] as ArrayBuffer,
        weight,
        style: "normal" as const,
      })),
    });
  })();
  return building;
}

/**
 * How often the preview redraws a component while something is playing.
 *
 * A draw is tens of milliseconds, so redrawing every frame would spend the
 * whole main thread on it. This is not the render being approximated: every
 * frame it does draw is exactly the frame the worker would make at that moment,
 * because both are the same function of the same time. What is coarser is how
 * many of them somebody sees while scrubbing past.
 *
 * Paused is different and is not throttled: whatever moment somebody has
 * stopped on is drawn exactly, because that is the frame they are looking at
 * when they decide whether the card is right.
 */
export const redrawEverySec = 0.1;

/**
 * The size a component's picture is drawn at.
 *
 * Fixed rather than the box's pixel size, so the picture does not change every
 * time somebody resizes the window. Named here rather than at the one call site
 * because placing that picture in its box needs its shape, and a component is
 * the one source whose shape is known before anything has loaded.
 */
export const componentPicture = { widthPx: 960, heightPx: 540 };
